import { useEffect, useState } from '@frontend/react';
import { Divider, Text, XStack, YStack, useApplePayRequest } from '@frontend/web-react';
import { useAnalytics, useStripeCard } from '@frontend/web/hooks';
import { PaymentRequestButtonElement } from '@stripe/react-stripe-js';
import type {
  PaymentRequestPaymentMethodEvent,
  StripeCardCvcElementChangeEvent,
  StripeCardExpiryElementChangeEvent,
  StripeCardNumberElementChangeEvent,
} from '@stripe/stripe-js';
import { forwardRef, useImperativeHandle } from 'react';
import { CardCvc, CardExpiry, CardNumber } from '../Payment';
import type { PaymentCaptureProps, PaymentCaptureRef } from './types';
import { usePaymentCapture } from './usePaymentCapture';

export const PaymentCapture = forwardRef<PaymentCaptureRef, PaymentCaptureProps>(
  (
    { amountCharged, nextBillingDate, onboardingSessionId, onError, onSuccess, planCode, planPrice, promoCode, step },
    ref,
  ) => {
    const { track } = useAnalytics();
    const [cardCompletion, setCardCompletion] = useState({
      cardCvc: false,
      cardExpiry: false,
      cardNumber: false,
    });
    const [hasTrackedCardEntryCompleted, setHasTrackedCardEntryCompleted] = useState(false);
    const [hasTrackedCardEntryStarted, setHasTrackedCardEntryStarted] = useState(false);
    const { setCardCvcInfo, setCardExpiryInfo, setCardNumberInfo } = useStripeCard();

    const { submitPayment } = usePaymentCapture({
      amountCharged,
      nextBillingDate,
      onError,
      onSuccess,
      onboardingSessionId,
      planCode,
      planPrice,
      promoCode,
      step,
    });

    const { canMakePayment, paymentRequest } = useApplePayRequest({ amountCents: amountCharged, label: 'Duckbill' });

    useEffect(() => {
      if (!paymentRequest) return;
      const handler = async (event: PaymentRequestPaymentMethodEvent) => {
        const success = await submitPayment(event.paymentMethod.id);
        event.complete(success ? 'success' : 'fail');
      };
      paymentRequest.on('paymentmethod', handler);
      return () => {
        paymentRequest.off('paymentmethod', handler);
      };
    }, [paymentRequest, submitPayment]);

    const handleCardEntryStarted = (hasUserInput: boolean) => {
      if (!hasUserInput || hasTrackedCardEntryStarted) {
        return;
      }

      setHasTrackedCardEntryStarted(true);
      track({
        data: {
          onboardingSessionId,
          planCode,
          platform: 'web',
          purchaseType: 'Subscription',
          step,
        },
        event: 'membershipCreditCardEntryStarted',
        topic: 'onboarding',
      });
    };

    useEffect(() => {
      if (
        !cardCompletion.cardCvc ||
        !cardCompletion.cardExpiry ||
        !cardCompletion.cardNumber ||
        hasTrackedCardEntryCompleted
      ) {
        return;
      }

      setHasTrackedCardEntryCompleted(true);
      track({
        data: {
          onboardingSessionId,
          planCode,
          platform: 'web',
          purchaseType: 'Subscription',
          step,
        },
        event: 'membershipCreditCardEntryCompleted',
        topic: 'onboarding',
      });
    }, [
      cardCompletion.cardCvc,
      cardCompletion.cardExpiry,
      cardCompletion.cardNumber,
      hasTrackedCardEntryCompleted,
      onboardingSessionId,
      planCode,
      step,
      track,
    ]);

    const handleCardCvcChange = (info: StripeCardCvcElementChangeEvent) => {
      handleCardEntryStarted(!info.empty);
      setCardCvcInfo(info);
      setCardCompletion((prev) => ({ ...prev, cardCvc: info.complete }));
    };

    const handleCardExpiryChange = (info: StripeCardExpiryElementChangeEvent) => {
      handleCardEntryStarted(!info.empty);
      setCardExpiryInfo(info);
      setCardCompletion((prev) => ({ ...prev, cardExpiry: info.complete }));
    };

    const handleCardNumberChange = (info: StripeCardNumberElementChangeEvent) => {
      handleCardEntryStarted(!info.empty);
      setCardNumberInfo(info);
      setCardCompletion((prev) => ({ ...prev, cardNumber: info.complete }));
    };

    useImperativeHandle(ref, () => ({
      submitPayment,
    }));

    return (
      <YStack gapY="md">
        {canMakePayment && paymentRequest ? (
          <YStack gapY="md">
            <PaymentRequestButtonElement options={{ paymentRequest }} />
            <Divider label="Or pay with card" labelPosition="center" />
          </YStack>
        ) : null}
        <YStack gapY="md">
          <CardNumber onChange={handleCardNumberChange} />
          <XStack gapX="md">
            <CardExpiry onChange={handleCardExpiryChange} />
            <CardCvc onChange={handleCardCvcChange} />
          </XStack>
        </YStack>

        <Text className="italic" type="caption">
          Duckbill takes security seriously. All transactions are processed by Stripe, so our team will never see your
          credit card information. Please visit our privacy policy for more details on how we handle sensitive
          information.
        </Text>
      </YStack>
    );
  },
);
